import { Button } from "@/components/base/buttons/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { CatalogRisk } from "./useSafetyConfigState";

type Blocker = {
  status: string;
  reset?: () => void;
  proceed?: () => void;
};

export const UnsavedChangesDialog = ({ blocker }: { blocker: Blocker }) => (
  <Dialog open={blocker.status === "blocked"} onOpenChange={() => blocker.status === "blocked" && blocker.reset?.()}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Unsaved changes</DialogTitle>
      </DialogHeader>
      <p className="text-sm text-secondary">You have unsaved changes. Are you sure you want to leave this page?</p>
      <DialogFooter>
        <Button color="secondary" size="sm" onClick={() => blocker.status === "blocked" && blocker.reset?.()}>
          Stay
        </Button>
        <Button size="sm" onClick={() => blocker.status === "blocked" && blocker.proceed?.()}>
          Leave
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

type DeleteCustomRiskDialogProps = {
  risk: CatalogRisk | null;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

export const DeleteCustomRiskDialog = ({ risk, isPending, onCancel, onConfirm }: DeleteCustomRiskDialogProps) => (
  <Dialog open={risk !== null} onOpenChange={(open) => !open && onCancel()}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Delete custom risk</DialogTitle>
      </DialogHeader>
      <p className="text-sm text-secondary">
        Delete <span className="font-medium text-primary">{risk?.readableName}</span> for this merchant? If no other
        merchant uses it, the risk is removed from the catalog entirely. This cannot be undone.
      </p>
      <DialogFooter>
        <Button color="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button color="primary" size="sm" isLoading={isPending} onClick={onConfirm}>
          Delete
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

type ResetFieldConfirmDialogProps = {
  open: boolean;
  fieldLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
};

export const ResetFieldConfirmDialog = ({ open, fieldLabel, onCancel, onConfirm }: ResetFieldConfirmDialogProps) => (
  <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
    <DialogContent className="bg-black border-gray-800">
      <DialogHeader>
        <DialogTitle className="text-white">Reset to default</DialogTitle>
      </DialogHeader>
      <p className="text-sm text-white">
        Discard the merchant-specific wording for <span className="font-medium text-white">{fieldLabel}</span> and use
        the Risk Catalog default instead? This can't be undone, though the field can be edited again afterward.
      </p>
      <DialogFooter>
        <Button color="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button color="primary" size="sm" onClick={onConfirm}>
          Reset
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
